import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import VendorLayout from "../../components/VendorLayout";
import { api, formatApiError } from "../../lib/api";
import { onDataChanged } from "../../lib/dataBus";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, ChevronRight, Sparkles, CalendarCheck, Users,
  Boxes, Banknote, ListChecks,
  Calendar as CalIcon, Filter, FileText, BarChart3,
  ChevronDown, Check, ArrowUpRight, ArrowDownLeft, Gauge, Search, CalendarClock,
  Plus, X, SlidersHorizontal,
} from "lucide-react";
import PilotageCalendar from "../../components/pilotage/PilotageCalendar";
import { FilterDropdown } from "../../components/pilotage/Filters";
import ViewToggle from "../../components/pilotage/ViewToggle";
import PlanningView from "../../components/pilotage/PlanningView";
import VendorBookings from "./Bookings";
import VendorBookingDetail from "./BookingDetail";
import BookingEditor from "../../components/pilotage/BookingEditor";
import VehicleDetailVendor from "./VehicleDetailVendor";
import VendorRevenue from "./Revenue";
import VendorClients from "./Clients";
import VendorVehicles from "./Vehicles";
import { FleetSection } from "./Parametrages";
import DevisView from "../../components/pilotage/DevisView";
import ContractFiche from "../../components/pilotage/ContractFiche";
import InvoiceFiche from "../../components/pilotage/InvoiceFiche";
import ClaimFiche from "../../components/pilotage/ClaimFiche";
import FineFiche from "../../components/pilotage/FineFiche";
import SupplierFiche from "../../components/pilotage/SupplierFiche";
import FleetEventsView from "../../components/pilotage/FleetEventsView";
import VendorStatistics from "./Statistics";
import { CopilotWorkspace } from "../../components/pilotage/CopilotChat";
import OpsToday from "../../components/pilotage/OpsToday";
import SegmentedPills from "../../components/ui/SegmentedPills";

const FR_MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const FR_DAYS_LONG = ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const addMonths = (d, n) => { const r = new Date(d); r.setMonth(r.getMonth()+n); return r; };
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate()+n); return r; };
const startOfWeek = (d) => { const r = new Date(d); const off = (r.getDay()+6)%7; r.setDate(r.getDate()-off); return r; };

// Compteur compact pour les badges d'onglets : 0 masqué, <1000 entier, ≥1000 → « 1,5k »
const fmtTabCount = (n) => {
  if (n === undefined || n === null || n <= 0) return null;
  if (n < 1000) return String(n);
  const k = n / 1000;
  return `${k.toFixed(k < 10 ? 1 : 0).replace(/\.0$/, "").replace(".", ",")}k`;
};
const OPTIONAL_KEYS = ["revenue", "stats"];

export default function VendorPilotage() {
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [connect, setConnect] = useState(null);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? "day" : "month")); // day | week | month
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const [tab, setTabState] = useState(
    initialTab && initialTab !== "tasks" ? initialTab : localStorage.getItem("jn_last_tab") || "calendar"
  );
  const setTab = (t) => { setTabState(t); localStorage.setItem("jn_last_tab", t); setSearchParams({ tab: t }, { replace: true }); };
  // Sync onglet ← URL (navigations internes : liens Détails, cartes de survol, devis → résa…)
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== "tasks" && t !== tab) setTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [copilotOpen, setCopilotOpenState] = useState(
    searchParams.get("mode") ? searchParams.get("mode") === "copilot" : localStorage.getItem("jn_last_mode") === "copilot"
  );
  const setCopilotOpen = (open) => { setCopilotOpenState(open); localStorage.setItem("jn_last_mode", open ? "copilot" : "junago"); };
  useEffect(() => {
    const m = searchParams.get("mode");
    if (m === "copilot") setCopilotOpen(true);
    if (m === "junago") setCopilotOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [cursor, setCursor] = useState(new Date());

  // ─── Onglets facultatifs (Revenus, Statistiques) — ouverts via le bouton + ───
  const [openOptional, setOpenOptional] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem("jn_optional_tabs") || "[]");
      return Array.isArray(s) ? s.filter((k) => OPTIONAL_KEYS.includes(k)) : [];
    } catch { return []; }
  });
  const persistOptional = (arr) => localStorage.setItem("jn_optional_tabs", JSON.stringify(arr));
  // Toute navigation vers un module facultatif (dernier onglet actif, lien Copilot, ?create=…) ouvre son onglet
  useEffect(() => {
    if (!OPTIONAL_KEYS.includes(tab)) return;
    setOpenOptional((prev) => {
      if (prev.includes(tab)) return prev;
      const next = [...prev, tab];
      persistOptional(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  const closeOptionalTab = (k) => {
    setOpenOptional((prev) => { const next = prev.filter((x) => x !== k); persistOptional(next); return next; });
    if (tab === k) setTab("calendar");
  };

  const [calendarMode, setCalendarMode] = useState("calendar"); // calendar | planning
  const [calFiltersOpen, setCalFiltersOpen] = useState(false); // repli des filtres secondaires sur mobile
  const [calPanelOpen, setCalPanelOpen] = useState(false); // panneau de filtres desktop (Gmail)
  const calSearchRef = useRef(null);
  useEffect(() => {
    if (!calPanelOpen) return;
    const onDown = (e) => { if (calSearchRef.current && !calSearchRef.current.contains(e.target)) setCalPanelOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setCalPanelOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [calPanelOpen]);

  // « Retour au Copilote » : visible uniquement après une navigation issue du chat (jamais de retour forcé)
  const [fromCopilot, setFromCopilot] = useState(false);
  const prevCopilotOpenRef = useRef(false);
  useEffect(() => {
    const was = prevCopilotOpenRef.current;
    prevCopilotOpenRef.current = copilotOpen;
    if (copilotOpen && !was) {
      // Ouverture (ou réouverture) du Copilote : le flag est consommé
      sessionStorage.removeItem("jn_from_copilot");
      setFromCopilot(false);
    } else if (!copilotOpen) {
      setFromCopilot(sessionStorage.getItem("jn_from_copilot") === "1");
    }
  }, [copilotOpen, searchParams]);
  const backToCopilot = () => {
    sessionStorage.removeItem("jn_from_copilot");
    setFromCopilot(false);
    setCopilotOpen(true);
  };

  const [locations, setLocations] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const detailBookingId = searchParams.get("booking");
  const detailVehicleId = searchParams.get("vehicle");
  const showContract = searchParams.get("contract") === "1";
  const showInvoiceId = searchParams.get("invoice");
  const showClaimId = searchParams.get("claim");
  const showFineId = searchParams.get("fine");
  const showSupplierId = searchParams.get("supplier");
  const showFleetEvents = searchParams.get("view") === "events";
  const fleetview = searchParams.get("fleetview") === "types" ? "types" : "ma-flotte";

  const loadAll = (opts = {}) => {
    if (!opts.silent) setLoading(true);
    Promise.all([
      api.get("/vendor/vehicles"),
      api.get("/vendor/bookings"),
      api.get("/vendor/stats"),
      api.get("/stripe/connect/status"),
      api.get("/vendor/locations"),
      api.get("/vendor/blocks").catch(() => ({ data: { blocks: [] } })),
    ])
      .then(([v, b, s, c, l, bl]) => {
        setVehicles(v.data.vehicles || []);
        setBookings(b.data.bookings || []);
        setStats(s.data);
        setConnect(c.data);
        setLocations(l.data.locations || []);
        setBlocks(bl.data.blocks || []);
      })
      .finally(() => { if (!opts.silent) setLoading(false); });
  };
  useEffect(() => { loadAll(); }, []);

  // Actualisation automatique (même session) : après une opération CRUD réussie
  // dans un module (flotte, clients, réservations, devis), les données partagées
  // (compteurs d'onglets, calendrier, sélecteurs de véhicules) sont rechargées en
  // silence — sans skeleton ni perte des filtres/dates/sélections en cours.
  useEffect(() => {
    let t;
    const off = onDataChanged(() => {
      clearTimeout(t);
      t = setTimeout(() => loadAll({ silent: true }), 300);
    });
    return () => { off(); clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshSystemTasks = () => {
    window.dispatchEvent(new Event("copilot-tasks-changed"));
    window.dispatchEvent(new Event("taskcenter-changed"));
  };

  // ─── Compteur de tâches IA (badge du bouton ⚡ haut-droite) ───
  const [aiTasksCount, setAiTasksCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get("/vendor/tasks/system")
        .then((r) => { if (alive) setAiTasksCount((r.data.tasks || []).length); })
        .catch(() => {});
    load();
    window.addEventListener("copilot-tasks-changed", load);
    return () => { alive = false; window.removeEventListener("copilot-tasks-changed", load); };
  }, []);

  // ─── Permissions (Équipe & Sécurité) : masquage des onglets — l'enforcement réel est côté backend ───
  const [perms, setPerms] = useState(null);
  useEffect(() => {
    api.get("/vendor/team/me/permissions").then((r) => setPerms(r.data)).catch(() => {});
  }, []);
  const TAB_PERM = {
    today: ["planning", "read"],
    calendar: ["planning", "read"], bookings: ["bookings", "read"], clients: ["clients", "read"],
    vehicles: ["vehicles", "read"], quotes: ["quotes", "read"], stats: ["reports", "read"], revenue: ["billing", "finance"],
  };
  const tabAllowed = (k) => {
    if (!perms || perms.is_owner) return true;
    const [m, a] = TAB_PERM[k] || [];
    return m ? !!perms.permissions?.[m]?.[a] : true;
  };

  const openCopilotTasks = () => {
    setCopilotOpen(true);
    setTimeout(() => window.dispatchEvent(new CustomEvent("copilot-run-action", { detail: "tasks" })), 450);
  };

  // ─── Bouton « 🤖 Analyser avec Junago Copilot » du module Statistiques ───
  const analyzeWithCopilot = (prompt) => {
    setCopilotOpen(true);
    setTimeout(() => window.dispatchEvent(new CustomEvent("copilot-send-text", { detail: prompt })), 500);
  };

  // ─── Ouverture du Copilot via ?copilot= (bouton ⚡ du rail) ou ancien lien ?tab=tasks ───
  useEffect(() => {
    const cp = searchParams.get("copilot");
    const legacyTasks = searchParams.get("tab") === "tasks";
    if (!cp && !legacyTasks) return;
    const next = new URLSearchParams(searchParams);
    next.delete("copilot");
    if (legacyTasks) next.set("tab", "calendar");
    setSearchParams(next, { replace: true });
    if (legacyTasks) setTabState("calendar");
    setCopilotOpen(true);
    if (cp === "tasks" || legacyTasks) {
      setTimeout(() => window.dispatchEvent(new CustomEvent("copilot-run-action", { detail: "tasks" })), 450);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const title = useMemo(() => {
    if (view === "day") return `${FR_DAYS_LONG[cursor.getDay()]} ${cursor.getDate()} ${FR_MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (view === "week") {
      const s = startOfWeek(cursor); const e = addDays(s, 6);
      return `${s.getDate()} ${FR_MONTHS[s.getMonth()].slice(0,3)} – ${e.getDate()} ${FR_MONTHS[e.getMonth()].slice(0,3)} ${e.getFullYear()}`;
    }
    if (view === "quarter") {
      const e = addMonths(cursor, 2);
      return `${FR_MONTHS[cursor.getMonth()]} – ${FR_MONTHS[e.getMonth()]} ${e.getFullYear()}`;
    }
    return `${FR_MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  }, [view, cursor]);

  const goPrev = () => setCursor(view === "day" ? addDays(cursor, -1) : view === "week" ? addDays(cursor, -7) : view === "quarter" ? addMonths(cursor, -3) : addMonths(cursor, -1));
  const goNext = () => setCursor(view === "day" ? addDays(cursor, 1) : view === "week" ? addDays(cursor, 7) : view === "quarter" ? addMonths(cursor, 3) : addMonths(cursor, 1));

  // ─── Calendrier : filtres (véhicules, agence, statut, client), compteur période & KPIs ───
  const [vehicleFilter, setVehicleFilter] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientSearch, setClientSearch] = useState("");
  const locationsById = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l])), [locations]);
  const enrichedBookings = useMemo(() => bookings.map((b) => ({
    ...b,
    pickup_location_name: b.pickup_location_id ? locationsById[b.pickup_location_id]?.name || null : null,
    return_location_name: b.return_location_id ? locationsById[b.return_location_id]?.name || null : null,
  })), [bookings, locationsById]);
  const periodRange = useMemo(() => {
    if (view === "day") { const d = new Date(cursor); return { start: d, end: d, nDays: 1 }; }
    if (view === "week") { const s = startOfWeek(cursor); return { start: s, end: addDays(s, 6), nDays: 7 }; }
    if (view === "quarter") {
      const s = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const e = new Date(cursor.getFullYear(), cursor.getMonth() + 3, 0);
      return { start: s, end: e, nDays: Math.round((e - s) / 86400000) + 1 };
    }
    const total = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    return { start: new Date(cursor.getFullYear(), cursor.getMonth(), 1), end: new Date(cursor.getFullYear(), cursor.getMonth(), total), nDays: total };
  }, [view, cursor]);
  const filteredVehicles = vehicles
    .filter((v) => !vehicleFilter.length || vehicleFilter.includes(v.id))
    .filter((v) => locationFilter === "all" || v.location_id === locationFilter);
  const visibleVehicleIds = new Set(filteredVehicles.map((v) => v.id));
  const clientQuery = clientSearch.trim().toLowerCase();
  const filteredBookings = enrichedBookings
    .filter((b) => visibleVehicleIds.has(b.vehicle_id))
    .filter((b) => statusFilter === "all" || b.status === statusFilter)
    .filter((b) => !clientQuery
      || (b.client_name || "").toLowerCase().includes(clientQuery)
      || (b.client_email || "").toLowerCase().includes(clientQuery)
      || (b.reference || "").toLowerCase().includes(clientQuery));
  const overlapDays = (b) => {
    const bs = new Date(b.start_date + "T00:00:00");
    const be = new Date(b.end_date + "T00:00:00");
    const s = bs > periodRange.start ? bs : periodRange.start;
    const e = be < periodRange.end ? be : periodRange.end;
    return e < s ? 0 : Math.round((e - s) / 86400000) + 1;
  };
  const periodBookings = filteredBookings.filter((b) => b.status !== "cancelled" && overlapDays(b) > 0);
  const todayStr = ymd(new Date());
  const kpiDepartures = filteredBookings.filter((b) => b.status !== "cancelled" && b.start_date === todayStr).length;
  const kpiReturns = filteredBookings.filter((b) => b.status !== "cancelled" && b.end_date === todayStr).length;
  const kpiOccupancy = filteredVehicles.length
    ? Math.min(100, Math.round((periodBookings.reduce((sum, b) => sum + overlapDays(b), 0) / (filteredVehicles.length * periodRange.nDays)) * 100))
    : 0;

  const changeBookingStatus = async (id, status) => {
    try {
      await api.patch(`/vendor/bookings/${id}`, { status });
      loadAll();
      toast.success("Statut mis à jour");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const changeBookingDates = async (id, start_date, end_date) => {
    try {
      await api.patch(`/vendor/bookings/${id}`, { start_date, end_date });
      loadAll();
      toast.success("Dates mises à jour");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const createBlock = async (vehicleId, payload) => {
    try {
      await api.post(`/vendor/vehicles/${vehicleId}/blocks`, payload);
      loadAll();
      toast.success("Période bloquée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const deleteBlock = async (blockId) => {
    try {
      await api.delete(`/vendor/blocks/${blockId}`);
      loadAll();
      toast.success("Blocage supprimé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };


  // ─── Consommation de ?create= (sidebar Copilot & cartes d'action IA) ───
  const lastCreateRef = useRef(0);
  useEffect(() => {
    const createParam = searchParams.get("create");
    if (!createParam) return;
    // Garde anti double-exécution (StrictMode) : les boutons toggle seraient refermés par un 2e clic
    if (Date.now() - lastCreateRef.current < 1500) return;
    lastCreateRef.current = Date.now();
    // Une action « créer X » se déroule toujours dans le tableau de bord : sortir du mode Copilote
    setCopilotOpen(false);
    // Consomme le paramètre
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    setSearchParams(next, { replace: true });

    const clickWhenReady = (testid, tries = 12) => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (el) { el.click(); return; }
      if (tries > 0) setTimeout(() => clickWhenReady(testid, tries - 1), 300);
    };
    const goEvents = (section) => {
      setSearchParams({ tab: "vehicles", view: "events", events: section });
      setTabState("vehicles");
      localStorage.setItem("jn_last_tab", "vehicles");
    };
    const goRevenue = (subTab) => {
      localStorage.setItem("jn_revenue_tab", subTab);
      setTab("revenue");
    };

    switch (createParam) {
      case "booking":
        setSearchParams({ tab: "bookings", booking: "new" });
        break;
      case "prospect":
        setTab("clients");
        setTimeout(() => clickWhenReady("add-prospect-btn"), 250);
        break;
      case "client":
        setTab("clients");
        setTimeout(() => clickWhenReady("add-client-btn"), 250);
        break;
      case "vehicle":
        setTab("vehicles");
        setTimeout(() => clickWhenReady("open-vehicle-form"), 250);
        break;
      case "quote":
        setTab("quotes");
        setTimeout(() => clickWhenReady("create-quote-btn"), 250);
        break;
      case "intervention":
        goEvents("maintenance");
        setTimeout(() => clickWhenReady("add-maintenance-global-btn"), 400);
        break;
      case "issue":
        goEvents("issues");
        break;
      case "fine":
        goEvents("fines");
        setTimeout(() => clickWhenReady("add-fine-btn"), 400);
        break;
      case "supplier":
        goEvents("suppliers");
        setTimeout(() => clickWhenReady("add-supplier-btn"), 400);
        break;
      case "purchase":
        goRevenue("depenses");
        setTimeout(() => clickWhenReady("add-purchase-btn"), 500);
        break;
      case "invoice":
        goRevenue("factures");
        setTimeout(() => clickWhenReady("add-free-invoice-btn"), 500);
        break;
      case "credit_note":
        goRevenue("factures");
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const PERMANENT_TABS = [
    ["today", "Aujourd'hui", CalendarClock],
    ["calendar", "Calendrier", CalIcon],
    ["bookings", "Réservations", CalendarCheck],
    ["vehicles", "Flotte", Boxes],
    ["clients", "Clients", Users],
    ["quotes", "Devis", FileText],
  ];
  const OPTIONAL_TABS = [
    ["revenue", "Revenus", Banknote],
    ["stats", "Statistiques", BarChart3],
  ];
  const tabCounts = {
    bookings: bookings.length,
    vehicles: vehicles.length,
    clients: stats?.clients_count,
    quotes: stats?.quotes_count,
  };

  return (
    <VendorLayout dark={copilotOpen} mode={copilotOpen ? "copilot" : "junago"} mainPadBottom={false}>
      <div className={`relative flex flex-col overflow-hidden lg:h-screen ${copilotOpen ? "h-[calc(100vh-56px)]" : "h-[calc(100vh-112px)]"}`}>
        <style>{`
          .jn-chrome-tab{background:#fff;box-shadow:0 -1px 2px rgba(15,23,42,0.04);}
          .jn-chrome-tab::before,.jn-chrome-tab::after{content:"";position:absolute;bottom:0;width:10px;height:10px;pointer-events:none;}
          .jn-chrome-tab::before{left:-10px;background:radial-gradient(circle at 0 0, transparent 10px, #fff 10.5px);}
          .jn-chrome-tab::after{right:-10px;background:radial-gradient(circle at 100% 0, transparent 10px, #fff 10.5px);}
        `}</style>
        {/* TABS BAR — style navigateur */}
        <div
          className={`shrink-0 border-b pl-3 pr-3 lg:pl-4 lg:pr-[560px] items-end gap-0.5 overflow-x-auto no-scrollbar transition-colors ${copilotOpen ? "flex" : "hidden lg:flex"} ${
            copilotOpen ? "border-white/10 bg-[#0B0B16]" : "border-slate-200 bg-slate-100"
          }`}
          data-testid="pilotage-tabs"
        >
          {!copilotOpen && fromCopilot && (
            <button
              onClick={backToCopilot}
              className="shrink-0 self-center inline-flex items-center gap-1.5 mr-2 px-3 h-8 rounded-full bg-violet-600 hover:bg-violet-500 text-white text-[11px] font-bold whitespace-nowrap transition-colors"
              data-testid="back-to-copilot-btn"
            >
              <Sparkles className="h-3.5 w-3.5" /> Retour au Copilote
            </button>
          )}
          {copilotOpen ? (
            <div className="inline-flex items-center gap-2 py-[13px] text-sm font-semibold text-white" data-testid="copilot-mode-title">
              <Sparkles className="h-4 w-4 text-violet-400" /> Junago Copilot
              <span className="inline-flex items-center gap-1 ml-2 text-[10px] font-bold text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> En ligne
              </span>
            </div>
          ) : (
            <>
              {PERMANENT_TABS.filter(([k]) => tabAllowed(k)).map(([k, l, Icon]) => (
                <PilotageTab
                  key={k} tabKey={k} label={l} Icon={Icon} active={tab === k}
                  count={fmtTabCount(tabCounts[k])} exact={tabCounts[k]}
                  onClick={() => { setTab(k); setCopilotOpen(false); }}
                />
              ))}
              {OPTIONAL_TABS.filter(([k]) => openOptional.includes(k) && tabAllowed(k)).map(([k, l, Icon]) => (
                <PilotageTab
                  key={k} tabKey={k} label={l} Icon={Icon} active={tab === k}
                  count={fmtTabCount(tabCounts[k])} exact={tabCounts[k]}
                  onClick={() => { setTab(k); setCopilotOpen(false); }}
                  onClose={() => closeOptionalTab(k)}
                />
              ))}
              <AddTabMenu
                optional={OPTIONAL_TABS}
                openOptional={openOptional}
                tabAllowed={tabAllowed}
                onPick={(k) => { setTab(k); setCopilotOpen(false); }}
              />
            </>
          )}
        </div>

        {/* Calendar-specific controls (only visible on Calendar tab) */}
        {tab === "calendar" && !copilotOpen && (
          <>
          {/* ───────── MOBILE / tablette portrait (< lg) — inchangé ───────── */}
          <div className="shrink-0 border-b border-slate-200 bg-white px-4 sm:px-6 py-3 flex flex-col gap-2 sm:gap-3 lg:hidden">
            {/* Ligne d'en-tête — réduite sur mobile (contents sur desktop) */}
            <div className="flex items-center gap-2 lg:contents">
              <h2 className="font-heading text-lg font-semibold text-slate-900 shrink-0">Calendrier</h2>
              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold shrink-0" data-testid="calendar-count">{periodBookings.length}</span>
              <span className="lg:hidden font-heading text-sm font-medium tracking-tight text-slate-700 capitalize truncate min-w-0">{title}</span>
              <button
                onClick={() => setCalFiltersOpen((o) => !o)}
                className="lg:hidden ml-auto shrink-0 relative h-9 w-9 rounded-xl border border-slate-200 bg-white text-slate-600 flex items-center justify-center transition-colors"
                data-testid="calendar-controls-toggle"
                aria-label="Afficher les commandes"
              >
                {(vehicleFilter.length > 0 || locationFilter !== "all" || statusFilter !== "all" || !!clientSearch) && <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-violet-600" />}
                <ChevronDown className={`h-4 w-4 transition-transform ${calFiltersOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
            {/* Commandes — repliables sur mobile */}
            <div className={`${calFiltersOpen ? "flex" : "hidden"} flex-wrap items-center gap-2 sm:gap-3`}>
              <div className="flex items-center gap-0.5 border border-slate-200 rounded-xl p-1 shrink-0">
                <button onClick={goPrev} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="pilotage-prev"><ChevronLeft className="h-4 w-4" /></button>
                <button onClick={() => setCursor(new Date())} className="px-3 h-8 rounded-lg text-xs font-medium hover:bg-slate-100" data-testid="pilotage-today">Aujourd'hui</button>
                <button onClick={goNext} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="pilotage-next"><ChevronRight className="h-4 w-4" /></button>
              </div>
              <SegmentedPills
                items={[
                  { key: "day", label: "Jour" },
                  { key: "week", label: "Semaine" },
                  { key: "month", label: "Mois" },
                  { key: "quarter", label: "3 mois" },
                ]}
                value={view}
                onChange={setView}
                testid="pilotage-view"
              />
              <SegmentedPills
                items={[
                  { key: "calendar", label: "Calendrier", icon: CalIcon },
                  { key: "planning", label: "Planning", icon: ListChecks },
                ]}
                value={calendarMode}
                onChange={setCalendarMode}
                testid="pilotage-mode"
              />
              <VehicleFilter vehicles={vehicles} selected={vehicleFilter} onChange={setVehicleFilter} />
              {locations.length > 1 && (
                <FilterDropdown
                  label="Agence"
                  value={locationFilter}
                  onChange={setLocationFilter}
                  options={[{ value: "all", label: "Toutes les agences" }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
                  testid="calendar-location-filter"
                />
              )}
              <FilterDropdown
                label="Statut"
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: "all", label: "Tous les statuts" },
                  { value: "confirmed", label: "Confirmées" },
                  { value: "pending", label: "En attente" },
                  { value: "completed", label: "Terminées" },
                  { value: "cancelled", label: "Annulées" },
                ]}
                testid="calendar-status-filter"
              />
              <div className="relative shrink-0 flex-1 min-w-[140px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                <input
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Client, réf…"
                  className="h-10 w-full pl-8 pr-2 rounded-xl border border-slate-200 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="calendar-client-search"
                />
              </div>
            </div>
          </div>

          {/* ───────── DESKTOP / tablette paysage (≥ lg) — harmonisé ───────── */}
          <div className="hidden lg:flex shrink-0 border-b border-slate-200 bg-white px-6 py-3 flex-col gap-3" data-testid="calendar-controls-desktop">
            {/* Ligne 1 : recherche + filtres ........ sélecteur Calendrier/Planning */}
            <div className="flex items-center gap-3">
              <div ref={calSearchRef} className="relative w-[320px] shrink-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <input
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Rechercher dans le calendrier..."
                  className="w-full h-10 pl-9 pr-11 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="calendar-search"
                />
                {(() => { const n = (vehicleFilter.length > 0 ? 1 : 0) + (statusFilter !== "all" ? 1 : 0) + (locationFilter !== "all" ? 1 : 0); return (
                  <button
                    type="button"
                    onClick={() => setCalPanelOpen((o) => !o)}
                    aria-label="Filtres"
                    title="Filtres"
                    className={`absolute right-1.5 inset-y-0 my-auto inline-flex items-center justify-center h-7 w-8 rounded-lg transition-colors ${n > 0 ? "text-violet-700 bg-violet-50" : "text-slate-500 hover:bg-slate-100"}`}
                    data-testid="calendar-filter-btn"
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    {n > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-violet-600 text-white text-[9px] font-bold flex items-center justify-center" data-testid="calendar-filter-count">{n}</span>}
                  </button>
                ); })()}
                {calPanelOpen && (
                  <div className="absolute left-0 top-full mt-2 z-40 w-max min-w-[260px] max-w-[min(560px,calc(100vw-7rem))] rounded-xl border border-slate-200 bg-white p-3 shadow-xl" data-testid="calendar-filters-panel">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Filtres</span>
                      {(vehicleFilter.length > 0 || statusFilter !== "all" || locationFilter !== "all") && (
                        <button onClick={() => { setVehicleFilter([]); setStatusFilter("all"); setLocationFilter("all"); }} className="text-[11px] font-semibold text-violet-600 hover:underline" data-testid="calendar-filters-clear">Effacer</button>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <VehicleFilter vehicles={vehicles} selected={vehicleFilter} onChange={setVehicleFilter} testid="calendar-vehicle-filter-d" />
                      {locations.length > 1 && (
                        <FilterDropdown
                          label="Agence"
                          value={locationFilter}
                          onChange={setLocationFilter}
                          options={[{ value: "all", label: "Toutes les agences" }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
                          testid="calendar-location-filter-d"
                        />
                      )}
                      <FilterDropdown
                        label="Statut"
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={[
                          { value: "all", label: "Tous les statuts" },
                          { value: "confirmed", label: "Confirmées" },
                          { value: "pending", label: "En attente" },
                          { value: "completed", label: "Terminées" },
                          { value: "cancelled", label: "Annulées" },
                        ]}
                        testid="calendar-status-filter-d"
                      />
                    </div>
                  </div>
                )}
              </div>
              <SegmentedPills
                items={[
                  { key: "day", label: "Jour" },
                  { key: "week", label: "Semaine" },
                  { key: "month", label: "Mois" },
                  { key: "quarter", label: "3 mois" },
                ]}
                value={view}
                onChange={setView}
                testid="calendar-period"
              />
              <div className="flex items-center gap-2 shrink-0">
                <div className="font-heading text-sm font-medium tracking-tight text-slate-900 capitalize px-1 whitespace-nowrap" data-testid="calendar-period-label">{title}</div>
                <div className="flex items-center gap-0.5 border border-slate-200 rounded-xl p-1 shrink-0">
                  <button onClick={goPrev} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="calendar-prev" aria-label="Période précédente"><ChevronLeft className="h-4 w-4" /></button>
                  <button onClick={() => setCursor(new Date())} className="px-3 h-8 rounded-lg text-xs font-medium hover:bg-slate-100" data-testid="calendar-today">Aujourd'hui</button>
                  <button onClick={goNext} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="calendar-next" aria-label="Période suivante"><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="flex-1" />
              <ViewToggle
                value={calendarMode}
                onChange={setCalendarMode}
                testid="calendar-viewmode"
                items={[
                  { key: "calendar", icon: CalIcon, label: "Calendrier" },
                  { key: "planning", icon: ListChecks, label: "Planning" },
                ]}
              />
            </div>
            {/* Indicateurs d'activité */}
            <div className="flex items-center gap-2" data-testid="calendar-kpis-desktop">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 text-[11px] font-semibold" data-testid="calendar-kpi-departures">
                <ArrowUpRight className="h-3 w-3" /> {kpiDepartures} départ{kpiDepartures > 1 ? "s" : ""} aujourd'hui
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 text-[11px] font-semibold" data-testid="calendar-kpi-returns">
                <ArrowDownLeft className="h-3 w-3" /> {kpiReturns} retour{kpiReturns > 1 ? "s" : ""} aujourd'hui
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold" data-testid="calendar-kpi-occupancy">
                <Gauge className="h-3 w-3" /> {kpiOccupancy}% occupation ({view === "day" ? "jour" : view === "week" ? "semaine" : view === "quarter" ? "3 mois" : "mois"})
              </span>
            </div>
          </div>
          </>
        )}

        {/* Main content */}
        <div className={`flex-1 min-h-0 overflow-hidden ${copilotOpen ? "bg-[#0B0B16]" : "bg-slate-50"}`}>
          {copilotOpen ? (
            <CopilotWorkspace onTasksChanged={refreshSystemTasks} />
          ) : loading ? (
            <div className="p-6 space-y-3" data-testid="calendar-skeleton">
              <div className="jn-skeleton h-8 w-64 rounded-lg" />
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="jn-skeleton h-14 w-full rounded-xl" />
              ))}
            </div>
          ) : tab === "today" ? (
            <OpsToday />
          ) : tab === "calendar" ? (
            calendarMode === "planning" ? (
              <PlanningView view={view} cursor={cursor} bookings={filteredBookings} vehicles={vehicles} blocks={blocks.filter((bl) => visibleVehicleIds.has(bl.vehicle_id))} onBookingStatusChange={changeBookingStatus} />
            ) : (
            <PilotageCalendar
              view={view}
              cursor={cursor}
              vehicles={filteredVehicles}
              bookings={filteredBookings}
              blocks={blocks.filter((bl) => visibleVehicleIds.has(bl.vehicle_id))}
              onQuickCreateBooking={(prefill) => {
                const p = { tab: "bookings", booking: "new" };
                if (prefill?.vehicle_id) p.vehicle = prefill.vehicle_id;
                if (prefill?.date) p.date = prefill.date;
                setSearchParams(p);
              }}
              onBookingStatusChange={changeBookingStatus}
              onBookingDatesChange={changeBookingDates}
              onBlockCreate={createBlock}
              onBlockDelete={deleteBlock}
            />
            )
          ) : tab === "bookings" ? (
            <div className="h-full overflow-y-auto">
              {detailBookingId === "new" ? (
                <BookingEditor
                  vehicles={vehicles}
                  locations={locations}
                  onBack={() => setSearchParams({ tab: "bookings" })}
                  onSaved={(b) => { loadAll(); setSearchParams(b?.id ? { tab: "bookings", booking: b.id } : { tab: "bookings" }); }}
                />
              ) : detailBookingId && showInvoiceId ? (
                <InvoiceFiche
                  invoiceId={showInvoiceId}
                  onBack={() => setSearchParams({ tab: "bookings", booking: detailBookingId })}
                />
              ) : showInvoiceId ? (
                <InvoiceFiche
                  invoiceId={showInvoiceId}
                  onBack={() => setSearchParams({ tab: "revenue" })}
                />
              ) : detailBookingId && showContract ? (
                <ContractFiche
                  bookingId={detailBookingId}
                  onBack={() => setSearchParams({ tab: "bookings", booking: detailBookingId })}
                />
              ) : detailBookingId ? (
                <VendorBookingDetail embedded embeddedId={detailBookingId} />
              ) : (
                <VendorBookings embedded />
              )}
            </div>
          ) : tab === "clients" ? (
            <div className="h-full overflow-hidden"><VendorClients embedded /></div>
          ) : tab === "vehicles" ? (
            <div className="h-full overflow-y-auto">
              {showClaimId ? (
                <ClaimFiche
                  claimId={showClaimId}
                  onBack={() => setSearchParams({ tab: "vehicles", view: "events" })}
                />
              ) : showFineId ? (
                <FineFiche
                  fineId={showFineId}
                  onBack={() => setSearchParams({ tab: "vehicles", view: "events" })}
                />
              ) : showSupplierId ? (
                <SupplierFiche
                  supplierId={showSupplierId}
                  onBack={() => setSearchParams({ tab: "vehicles", view: "events" })}
                />
              ) : showFleetEvents ? (
                <FleetEventsView vehicles={vehicles} initialView={searchParams.get("events") || undefined} onBack={() => setSearchParams({ tab: "vehicles" })} />
              ) : detailVehicleId ? <VehicleDetailVendor embedded embeddedId={detailVehicleId} /> : (
                <VendorVehicles embedded />
              )}
            </div>
          ) : tab === "quotes" ? (
            <DevisView vehicles={vehicles} locations={locations} />
          ) : tab === "stats" ? (
            <div className="h-full overflow-y-auto"><VendorStatistics onAnalyzeWithCopilot={analyzeWithCopilot} /></div>
          ) : (
            <div className="h-full overflow-y-auto"><VendorRevenue embedded /></div>
          )}
        </div>
      </div>
    </VendorLayout>
  );
}

// ─── Filtre véhicules du calendrier (multi-sélection) ─────────────────────────────────────────────────
function VehicleFilter({ vehicles, selected, onChange, testid = "calendar-vehicle-filter" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border text-xs font-medium transition-colors ${
          selected.length ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
        }`}
        data-testid={testid}
      >
        <Filter className="h-3.5 w-3.5" /> Véhicules{selected.length ? ` (${selected.length})` : ""} <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-11 z-40 w-72 max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl p-1.5" data-testid={`${testid}-panel`}>
          <button
            onClick={() => onChange([])}
            className="w-full text-left px-2.5 py-2 text-xs font-semibold text-violet-600 hover:bg-violet-50 rounded-lg"
            data-testid={`${testid}-all`}
          >
            Tous les véhicules
          </button>
          {vehicles.map((v) => (
            <button
              key={v.id}
              onClick={() => toggle(v.id)}
              className="w-full flex items-center gap-2 px-2.5 py-2 text-xs text-slate-700 hover:bg-slate-50 rounded-lg"
              data-testid={`${testid}-opt-${v.id}`}
            >
              <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${selected.includes(v.id) ? "bg-violet-600 border-violet-600" : "border-slate-300"}`}>
                {selected.includes(v.id) && <Check className="h-3 w-3 text-white" />}
              </span>
              <span className="truncate">{v.brand} {v.model}</span>
              <span className="ml-auto text-[9px] text-slate-400 font-mono shrink-0">{v.license_plate || ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Onglet « type Chrome » (permanent ou facultatif avec croix) ─────────────────────────────────────
function PilotageTab({ tabKey, label, Icon, active, count, exact, onClick, onClose }) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={`relative inline-flex items-center gap-1.5 mt-1.5 h-9 pl-3 ${onClose ? "pr-1.5" : "pr-3"} text-[13px] font-medium whitespace-nowrap transition-colors ${
        active ? "jn-chrome-tab rounded-t-[10px] text-slate-900 z-10" : "rounded-lg mb-1 text-slate-500 hover:bg-slate-200/70 hover:text-slate-800"
      }`}
      data-testid={`pilotage-tab-${tabKey}`}
    >
      <span className={`inline-flex items-center justify-center rounded-[5px] shrink-0 ${active ? "bg-violet-100 text-violet-700" : "bg-slate-200/80 text-slate-500"}`} style={{ height: 18, width: 18 }}>
        <Icon className="h-3 w-3" />
      </span>
      <span>{label}</span>
      {count && (
        <span
          title={exact != null ? `${exact}` : undefined}
          data-testid={`pilotage-tab-count-${tabKey}`}
          className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold tabular-nums bg-slate-200 text-slate-600`}
        >
          {count}
        </span>
      )}
      {onClose && (
        <span
          role="button"
          tabIndex={0}
          aria-label={`Fermer ${label}`}
          title="Fermer l'onglet"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onClose(); } }}
          className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-colors"
          data-testid={`pilotage-tab-close-${tabKey}`}
        >
          <X className="h-3 w-3" />
        </span>
      )}
    </button>
  );
}

// ─── Bouton + façon Chrome : menu (portal pour échapper au scroll horizontal des onglets) ───
function AddTabMenu({ optional, openOptional, tabAllowed, onPick }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, left: r.left });
    setOpen(true);
  };
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const closed = optional.filter(([k]) => tabAllowed(k) && !openOptional.includes(k));
  return (
    <div className="relative self-center ml-1 shrink-0">
      <button
        ref={btnRef}
        onClick={() => (open ? setOpen(false) : openMenu())}
        title="Ouvrir un module"
        aria-label="Ouvrir un module"
        className="inline-flex items-center justify-center h-7 w-7 rounded-full text-slate-500 hover:bg-slate-200/80 hover:text-slate-700 transition-colors"
        data-testid="pilotage-add-tab"
      >
        <Plus className="h-4 w-4" />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 1000 }}
          className="w-52 bg-white border border-slate-200 rounded-xl shadow-xl p-1.5"
          data-testid="pilotage-add-menu"
        >
          {closed.length ? closed.map(([k, l, Icon]) => (
            <button
              key={k}
              onClick={() => { onPick(k); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              data-testid={`pilotage-add-${k}`}
            >
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-slate-100 text-slate-500"><Icon className="h-3 w-3" /></span>
              {l}
            </button>
          )) : (
            <div className="px-2.5 py-2 text-[11px] text-slate-400" data-testid="pilotage-add-empty">Tous les modules sont ouverts</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
